import { useContext, useLayoutEffect, useRef, useState } from 'react';
import { TabPanelHiddenContext } from '../context/TabPanelHiddenContext';

/**
 * Отложенный маунт тяжёлого содержимого до первого показа: пока контейнер (`containerRef`) лежит в неактивной панели
 * `Tabs` (`display: none`), `shouldMount` — `false`; показали — `true` навсегда, скрытие не размонтирует. Видимость
 * проверяется при смене `activationKey` (активная вкладка) и при готовности содержимого (`ready`).
 *
 * Внутри `Tabs` видимость панели известна уже в рендере (`TabPanelHiddenContext`): содержимое видимой панели строится
 * тем же рендером. Проверка DOM в layout-эффекте ставила бы state после коммита — и грид в сотни компонентов строился
 * вложенным синхронным коммитом следом за формой. Вне `Tabs` остаётся проверка DOM.
 */
export const useMountOnFirstVisible = <T extends HTMLElement>(activationKey: unknown, ready = true) => {
    const containerRef = useRef<T>(null);
    const panelHidden = useContext(TabPanelHiddenContext);
    const visibleInRender = ready && panelHidden === false;
    const [shouldMount, setShouldMount] = useState(visibleInRender);
    if (visibleInRender && !shouldMount) setShouldMount(true);

    useLayoutEffect(() => {
        if (panelHidden !== undefined) return;
        if (ready && !shouldMount && !containerRef.current?.closest('.rf-tabs__content_hide')) {
            setShouldMount(true);
        }
    }, [activationKey, ready, shouldMount, panelHidden]);

    return { containerRef, shouldMount };
};
